import React from 'react';
import classnames from 'classnames';

import "./reading-stack.scss";
import { ProductCard } from '../../../ts/components/common/productCard/ProductCard';
import { IProductDescription } from '../../../ts/models/product/IProductDescription';
import { ManningOnlineStore } from '../../store/ManningOnlineStore';
import { IReadingList } from '../../models/ReadingList';
import ProductCover from '../../../ts/components/common/productCover/ProductCover';
import { ImageDisplayType } from '../../../ts/components/common/PlaceholderImage';

export enum ReadingStackDisplayType {
    Stacked,
    StackedInRow,
    Boxed,
    Tiled
}

export interface IProps {
    store: ManningOnlineStore;
    readingList: IReadingList;
    displayType: ReadingStackDisplayType;

    onRemoveReadingList?: (readingList: IReadingList) => void;
    onSelectReadingList?: (readingList: IReadingList) => void;
    
    onHandlePointerStart?: (e) => void;
    onAddToReadingList?: (productId: any) => void;

    onTitleDoubleClick?: () => void;
}
export interface IState {
    isBeingDraggedOn: boolean
}

export class ReadingStack extends React.Component <IProps, IState> {
    private hasBeenMounted: boolean;
    private root: HTMLElement;
    
    constructor(props: IProps) {
        super(props);

        this.handleDragEnter = this.handleDragEnter.bind(this);
        this.handleDragOver = this.handleDragOver.bind(this);
        this.handleDragLeave = this.handleDragLeave.bind(this);
        this.handleDragDrop = this.handleDragDrop.bind(this);
        this.handleRef = this.handleRef.bind(this);
        this.handleEveryReadingListDragEnter = this.handleEveryReadingListDragEnter.bind(this);
        this.handleReadingListClick = this.handleReadingListClick.bind(this);
        this.handleRemoveReadingList = this.handleRemoveReadingList.bind(this);

        this.state = {
            isBeingDraggedOn: false
        };
    }

    public componentDidMount(){
        this.hasBeenMounted = true;

        this.props.store.onEveryReadingListDragEnter(this.handleEveryReadingListDragEnter);
    }

    public componentWillUnmount(){
        this.hasBeenMounted = false;

        this.props.store.offEveryReadingListDragEnter(this.handleEveryReadingListDragEnter);
    }

    public render(){
        const readingList = this.props.readingList;
        if(readingList.products == null || readingList.products.length == 0) { return null; }

        switch(this.props.displayType){
            case ReadingStackDisplayType.Tiled:
                return this.renderTiledProductList();
            case ReadingStackDisplayType.Boxed:
                return this.renderBoxedProductList()
            case ReadingStackDisplayType.Stacked:
            case ReadingStackDisplayType.StackedInRow:
            default:
                return this.renderStackedProductList();
            
        }
    }

    private renderStackedProductList(){
        const readingList = this.props.readingList;
        if(readingList.products == null || readingList.products.length == 0) { return null; }
        
        const primaryProduct = readingList.products[readingList.products.length - 1];
        
        const messageForCover = this.getMessageForCover();
        const isStackedInRow = this.props.displayType == ReadingStackDisplayType.StackedInRow;

        const nextToLastProduct = readingList.products.length > 1 ? readingList.products[readingList.products.length - 2]: null;
        const nextToNextToLastProduct = readingList.products.length > 2 ? readingList.products[readingList.products.length - 3] : null;
        return <div className={classnames("reading-stack-root is-stacked", {"_is-showing-message-cover": !!messageForCover, "_is-being-dragged-on": this.state.isBeingDraggedOn, "is-stacked-in-row": isStackedInRow})}
                    ref={this.handleRef}
                    key={readingList.id + readingList.title}
                    style={!isStackedInRow ? {top: readingList.position != null ? readingList.position.y : 0, left: readingList.position != null ? readingList.position.x : 0} : {}}
                    data-reading-list-id={readingList.id}
                    onMouseDown={this.props.onHandlePointerStart}
                    onTouchStart={this.props.onHandlePointerStart}
                    
                    onDragEnter={this.handleDragEnter}
                    onDragOver={this.handleDragOver}
                    onDragLeave={this.handleDragLeave}
                    onDrop={this.handleDragDrop}>
                    
                    { nextToLastProduct != null ? <img className={classnames("_back-cover", {"wide-cover": nextToLastProduct.isWideCover})} width="80px" height="135px" alt="cover" src={nextToLastProduct.coverPath}></img> : null}
                    { nextToNextToLastProduct ? <img className={classnames("_back-cover", {"wide-cover": nextToNextToLastProduct.isWideCover})} width="80px" height="135px" alt="cover" src={nextToNextToLastProduct.coverPath}></img> : null}
                    
                    {primaryProduct != null 
                    && <ProductCard key={readingList.title + primaryProduct.id}
                                    productDescription={primaryProduct} 
                                    coverPlaceholder={this.props.store.coverPlaceholder}
                                    onCloseButtonClick={!isStackedInRow ? this.handleRemoveReadingList : null}
                                    wideCoverPlaceholder={this.props.store.wideCoverPlaceholder}
                                    forcedTitle={this.determineTitle(primaryProduct)}
                                    onHoverLabel={readingList.products.length == 1 ? "resume" : ""}
                                    centralIcon={readingList.products.length > 1 ? "fa-external-link" : ""}
                                    onCentralIconClick={this.handleReadingListClick}
                                    onTitleDoubleClick={this.props.onTitleDoubleClick}/> }

                    <div className="_message-cover">
                        <div className="_message" dangerouslySetInnerHTML={{__html: messageForCover}}></div>
                    </div>
               </div>
    }

    private renderTiledProductList(){
        const readingList = this.props.readingList;
        if(readingList.products == null || readingList.products.length == 0) { return null; }
        
        const messageForCover = this.getMessageForCover();
        const isStackedInRow = this.props.displayType == ReadingStackDisplayType.StackedInRow;
        return <div className={classnames("reading-stack-root tiled-reading-list-container", {"_is-showing-message-cover": !!messageForCover, "_is-being-dragged-on": this.state.isBeingDraggedOn, "is-stacked-in-row": isStackedInRow})}
                    ref={this.handleRef}
                    key={readingList.id + readingList.title}
                    style={!isStackedInRow ? {top: readingList.position != null ? readingList.position.y : 0, left: readingList.position != null ? readingList.position.x : 0} : {}}
                    data-reading-list-id={readingList.id}
                    onMouseDown={this.props.onHandlePointerStart}
                    onTouchStart={this.props.onHandlePointerStart}
                    
                    onDragEnter={this.handleDragEnter}
                    onDragOver={this.handleDragOver}
                    onDragLeave={this.handleDragLeave}
                    onDrop={this.handleDragDrop}>
                    
                    <div className='tiled-reading-list-title'>{readingList.title}</div>
                    <div className='tiled-reading-list-group'>
                        {Array(4).fill(null).map((_, index) => (
                            readingList.products[index] 
                            ? <img className='tiled-reading-list-cover' key={index} src={readingList.products[index].coverPath} alt={readingList.products[index].title} />
                            : <div className='tiled-reading-list-cover-blank' key={index}></div>
                            ))}
                            <div className="_central-icon tiled-reading-list-external-link" onClick={this.handleReadingListClick}><i className="fa fa-external-link"></i></div>
                    </div>
                    <div className='tiled-reading-list-num-tiles'>{readingList.products.length} titles</div>
               </div>
    }

    private renderBoxedProductList(){
        const readingList = this.props.readingList;
        if(readingList.products == null || readingList.products.length == 0) { return null; }

        const messageForCover = this.getMessageForCover();

        return <div className={classnames("reading-stack-root is-boxed", {
                    "_is-showing-message-cover": !!messageForCover, 
                    "_is-being-dragged-on": this.state.isBeingDraggedOn,
                    "_layout-one": readingList.products.length == 1
                })}
                    ref={this.handleRef}
                    key={readingList.id + readingList.title}
                    data-reading-list-id={readingList.id}
                    onClick={this.handleReadingListClick}
                    
                    onDragEnter={this.handleDragEnter}
                    onDragOver={this.handleDragOver}
                    onDragLeave={this.handleDragLeave}
                    onDrop={this.handleDragDrop}>
                    
                    {this.renderBoxedProducts(readingList)}
                    {this.renderTitle()}

                    <div className="_message-cover">
                        <div className="_message" dangerouslySetInnerHTML={{__html: messageForCover}}></div>
                    </div>
                    
                    <div className='_central-container'>
                        <i className='fa fa-external-link'/>
                    </div>
               </div>
    }

    private renderBoxedProducts(readingList: IReadingList){
        const products = readingList.products;
        let children = [];

        if(products.length == 0){ return null; }
        else if(products.length == 1){ children.push(this.renderProduct(products[0], products.length)); } 
        else if(products.length <= 4){ 
            children = products.map((product, index) => this.renderProduct(product, products.length));
            while(children.length < 4){
                children.push(<div className='placeholder' key={`placeholder-${children.length}`}></div>);
            }
        }
        else {
            const lastThree = products.slice(products.length - 3);
            let beginning = products.slice(0, products.length - 3);

            if(beginning.length > 3){ beginning = beginning.slice(beginning.length - 3); }

            children.push(<div className='_internal-stack' key={"internal-stack"}>
                            {beginning.map(it => this.renderProduct(it))}
                         </div>);

            lastThree.forEach(it => children.push(this.renderProduct(it)))
        }

        if(children.length == 0) { return null; }

        return <div className='_internal-grid'>{children}</div>;
    }

    private renderProduct(product: IProductDescription, totalChildrenCount: number = 0){
        let height = product.isWideCover ? 44 : 62;
        let width = 48;

        if(totalChildrenCount == 1 && !product.isWideCover){
            height = 133;
            width = 107;
        }
        
        return <ProductCover placeholderImage = {product.isWideCover ? this.props.store.wideCoverPlaceholder : this.props.store.coverPlaceholder}
                             imageDisplayType = {ImageDisplayType.Image}
                             fullImage = {product.coverPath}
                             height={height} width={width}
                             placeholderOnlyOnFail={false}
                             className="book-cover"
                             isLiveaudio={product.isLiveaudio}
                             key={product.manningId}/>
    }

    private handleRef(root: HTMLElement){
        this.root = root;
    }

    private getMessageForCover(): string {
        if(this.props.readingList.isSyncing) { return `<i class='fa fa-circle-notch fa-spin'></i>`}
        if(this.state.isBeingDraggedOn) { return "add to this stack"; }
    }

    private renderTitle() {
        const readingList = this.props.readingList;
        let title = readingList.title;
        if(!title && readingList.products.length > 0) {
            title = readingList.products[0].title;
        }
        
        return <>
                    <div className='reading-stack-title'>{title}</div>
                    <div className='reading-stack-subtitle'>{readingList.products.length} title{readingList.products.length > 1 ? "s" : ""}</div>
               </>
    }
        
    private determineTitle(primaryProduct: IProductDescription): string {
        const readingList = this.props.readingList;
        if(primaryProduct == null || readingList.products.length <= 1) { return ""; }
        
        let title = this.props.readingList.title;
        if(!title){
            title = readingList.products.map(it => it.title).reverse().join(" & ");
        }
        
        return `<span class='reading-stack-title'>
                    <span class='_title'>(${readingList.products.length}) ${title}</span>
                </span>` ;
    }

    private handleEveryReadingListDragEnter(readingListId: number){
        if(this.hasBeenMounted && this.props.readingList.id != readingListId){
            this.setState({
                isBeingDraggedOn: false
            });    
        }
    }

    private handleDragEnter(e){
        e.preventDefault();
        
        this.setState({
            isBeingDraggedOn: true
        });

        this.props.store.triggerEveryReadingListDragEnter(this.props.readingList.id);
    }

    private handleDragOver(e){
        e.preventDefault();
    }

    private handleDragLeave(e){
        if(this.root && this.root.contains(e.target)) { return; }
        
        this.setState({
            isBeingDraggedOn: false
        });
    }

    private handleDragDrop(e){
        e.preventDefault();
        e.stopPropagation();
        
        this.setState({
            isBeingDraggedOn: false
        });

        this.props.onAddToReadingList(JSON.parse(e.dataTransfer.getData("text")).productId);
    }

    private handleReadingListClick(){
        this.props.onSelectReadingList && this.props.onSelectReadingList(this.props.readingList);
    }

    private handleRemoveReadingList(){
        this.props.onRemoveReadingList && this.props.onRemoveReadingList(this.props.readingList);
    }
}